import { FC, useState } from 'react';
import { RowSelectionState } from '@tanstack/react-table';
import Wizard from '@/components/Wizard/Wizard';
import { StepCount } from '@/components/Wizard/Wizard.types';
import { SuccessPage } from './NavigationCodeSteps/SuccessPage';
import { NavigationCodeName } from './NavigationCodeSteps/NavigationCodeName';
import { BundlePriceSelection } from './NavigationCodeSteps/BundlePriceSelection';
import { useAddNavigationCodes, useBundleDetails } from '@/services/bundles/bundles.service';
import { useParams } from 'react-router-dom';
import { showToast } from '@/components/Toast/Toast';
import { ToastTypes } from '@/components/Toast/Toast.types';
import { useTranslation } from 'react-i18next';

const CreateNavCode: FC = () => {
  const { t } = useTranslation();
  const { bundleId } = useParams();
  const { data: bundleData } = useBundleDetails(bundleId !== undefined, bundleId!);
  const addNavigationCodesMutation = useAddNavigationCodes(bundleId !== undefined, bundleId!);
  const [step, setStep] = useState<StepCount>(0);
  const [codeName, setCodeName] = useState<string>('');
  const [valuesList, setValuesList] = useState<string[]>([]);
  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});
  const [showSuccessPage, setShowSuccessPage] = useState(false);

  const handleInputChange = (event: any) => {
    setCodeName(event.target.value);
  };

  const handleInputKeyDown = (event: any) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      const trimmedValue = codeName.trim();
      if (trimmedValue && !valuesList.includes(trimmedValue)) {
        setValuesList([...valuesList, trimmedValue]);
        setCodeName('');
      }
    }
  };

  const newArrayPrices = bundleData?.data.availablePrices.map((item) => ({ price: item }));
  const keys = Object.keys(selectedRows);
  const selectedPrices: { price: number }[] = [];
  if (newArrayPrices) {
    keys.forEach((key: any) => selectedPrices.push(newArrayPrices[key]));
  }

  const onClose = () => {
    setStep(0);
    window.location.href = `/bundles/${bundleId}/viewNavCode`;
  };

  const handleSubmit = () => {
    addNavigationCodesMutation.mutateAsync(
      {
        prices: selectedPrices.map((p) => p.price),
        navCode: valuesList,
      },
      {
        onSuccess: () => {
          setShowSuccessPage(true);
        },
        onError: () => {
          showToast({
            label: t('common.bundle.navCodeErrorMsg'),
            id: 'errorCreatingLinkedBundles',
            toastType: ToastTypes.Error,
          });
        },
      },
    );
  };

  return !showSuccessPage ? (
    <Wizard
      data-testid="createNavCode"
      currentStep={step}
      showStepIndicator
      steps={[
        {
          content: (
            <BundlePriceSelection
              bundleData={newArrayPrices}
              selectedRows={selectedRows}
              setSelectedRows={setSelectedRows}
              selectedBundles={selectedPrices}
            />
          ),
          title: t('common.bundle.navCode'),
          description: t('common.bundle.selectPrices'),
          onBackClick: onClose,
          onNextClick: () => setStep(1),
          nextButtonDisabled: !selectedPrices,
        },
        {
          content: (
            <NavigationCodeName
              codeName={codeName}
              valuesList={valuesList}
              handleInputChange={handleInputChange}
              handleInputKeyDown={handleInputKeyDown}
            />
          ),
          title: t('common.bundle.newNCode'),
          description: '',
          onBackClick: () => setStep(0),
          onNextClick: handleSubmit,
          nextButtonText: 'Confirm',
        },
      ]}
      isOpen
      handleClose={onClose}
    />
  ) : (
    <SuccessPage setShowSuccessPage={setShowSuccessPage} />
  );
};

export default CreateNavCode;
